import { useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { InsertCustomManufacturerInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { InsertCustomManufacturerInProductListingMutation } from 'api/graphQL/mutations/projectNugget.mutations'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useCallback, useMemo, useRef } from 'react'
import { v4 as newGuid } from 'uuid'
import { useNotification } from '../../../services'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import {
    useShallowSelector,
    useWorkspace2Actions,
} from '../../../Workspace2Store'
import { buildBodTwinPill } from './buildBodTwinPill'

const SAVING_NOTIFICATION_MESSAGE = 'Saving...'
const SAVE_SUCCESS_MESSAGE = 'Changes Saved Successfully'

interface OptimisticPillParams {
    manufacturerName: string
    existingPills: Partial<ContentItemViewNodesType>[]
    isBOD: boolean
    productFamilyId: string
    projectId: string
    fallbackOrder: number
}

/**
 * Creates a pill node for optimistic UI updates. The cast is intentional —
 * we build a minimal object matching the API shape while ContentItemViewNodesType
 * requires many properties that only the server fully populates.
 *
 * `order` is set so the new pill lands in its alphabetical slot among siblings:
 * predecessor (last sibling whose text precedes the new name) order + 1.
 * TreeDataMapper sorts children by `order`, which renders the pill in place.
 */
export const createOptimisticPillNode = ({
    manufacturerName,
    existingPills,
    isBOD,
    productFamilyId,
    projectId,
    fallbackOrder,
}: OptimisticPillParams): Partial<ContentItemViewNodesType> => {
    const optimisticId = newGuid()
    const pillElementId = newGuid()
    const lastPill = existingPills[existingPills.length - 1]
    const compositeId = `${optimisticId}_${
        lastPill?.dtvContentItemParentId ?? ''
    }`

    const trimmedName = manufacturerName.trim()
    const alphabeticalPredecessor = existingPills.reduce<
        Partial<ContentItemViewNodesType> | undefined
    >((best, pill) => {
        const pillText = (pill.elements?.[0]?.text ?? pill.text ?? '').trim()
        const comesBefore =
            pillText.localeCompare(trimmedName, undefined, {
                sensitivity: 'base',
            }) < 0
        if (!comesBefore) return best
        return (pill.order ?? 0) > (best?.order ?? -Infinity) ? pill : best
    }, undefined)
    const order = alphabeticalPredecessor
        ? (alphabeticalPredecessor.order ?? 0) + 1
        : existingPills.length > 0
        ? 0
        : fallbackOrder

    return {
        id: compositeId,
        key: compositeId,
        contentType: lastPill?.contentType ?? 'PRODUCTFAMILY',
        isManufacturers: !isBOD,
        isBOD,
        isCustomManufacturer: true,
        isPillSelected: true,
        isHidden: false,
        type: lastPill?.type ?? 'NUGGET',
        textType: 'Paragraph',
        order,
        elements: [
            {
                id: pillElementId,
                isOptionItem: true,
                optionItemState: 'InActive',
                text: manufacturerName,
                type: 'ReplacementOption',
                placeholderText: manufacturerName,
                elements: [],
            },
        ],
        text: manufacturerName,
        pillId: optimisticId,
        isUserAdded: false,
        isDeleted: false,
        productFamilyId,
        projectId,
        dtvParentId: lastPill?.dtvParentId ?? '',
        dtvContentItemParentId: lastPill?.dtvContentItemParentId ?? '',
        documentTemplateViewId: lastPill?.documentTemplateViewId ?? '',
        documentTemplateViewItemId: lastPill?.documentTemplateViewItemId ?? '',
        dtvItemId: lastPill?.dtvItemId ?? optimisticId,
        dtvContentItemId: optimisticId,
        partType: lastPill?.partType ?? 'Products',
        propertyRefId: '',
        paragraphOrder: lastPill?.paragraphOrder ?? 0,
        levelOfSpecificationTag: lastPill?.levelOfSpecificationTag ?? 0,
        isSharedProperty: false,
        isSustainabilityContentItem: false,
        isSelectedSustainabilityContentItem: false,
        isForProductType: lastPill?.isForProductType ?? true,
        isProductType: false,
        isProductGroup: false,
        isSharedProductTypeProperty: false,
        isProductGroupHasMultipleProductTypes: false,
        isTable: false,
        activatedManually: false,
        isProductTypeNested: false,
        isAddedToProjectProductType: false,
        isCreatedFromPremiumPlus: false,
        isAddedToProject: true,
        isImported: false,
        refBodItemId: '',
        comments: [],
        parentComments: [],
    } as Partial<ContentItemViewNodesType>
}

interface UseAddCustomManufacturerArgs {
    isBOD: boolean
    productFamilyId: string
    projectId: string
    elementId: string
    /** Root article to refetch after insert so the optimistic pill's
     * client-generated pillId is replaced with the server's real one
     * (see Bug: deleting a just-added custom manufacturer before a page
     * refresh fails with "CustomManufacturerProductId does not exist"). */
    rootArticleId?: string
}

/**
 * Encapsulates the optimistic add flow for a custom manufacturer pill:
 *  1. Build the new pill in its alphabetical slot among siblings.
 *  2. Shift trailing siblings' `order` up by 1 to break collisions.
 *  3. Mirror the new pill under the BOD paragraph (MFR-side adds only)
 *     so the manufacturer appears under both paragraphs immediately
 *     — see Bug 2684561 / `buildBodTwinPill`.
 *  4. Fire the insert mutation in the background.
 */
export const useAddCustomManufacturer = ({
    isBOD,
    productFamilyId,
    projectId,
    elementId,
    rootArticleId,
}: UseAddCustomManufacturerArgs) => {
    const { contentItemViewNodes, activeTab } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
        activeTab: state.activeTab,
    }))
    const {
        setContentItemViewNodes,
        appendContentIdsToReplaceOnNextFetch,
        addPendingAddPillId,
        removePendingAddPillId,
    } = useWorkspace2Actions()
    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        projectId,
    })

    // Carries the pre-optimistic snapshot and pill IDs across from the call
    // site to onError so the rollback can restore the tree and clear pending
    // state without closing over stale per-call locals.
    const pendingRollbackRef = useRef<{
        previousNodes: Partial<ContentItemViewNodesType>[]
        pillIds: string[]
    } | null>(null)

    const notification = useNotification()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [insertCustomManufacturerInProductListing] = useMutation<
        { insertCustomManufacturerInProductListing: ContentItem[] },
        { input: Partial<InsertCustomManufacturerInput> }
    >(InsertCustomManufacturerInProductListingMutation, {
        onError: (error) => {
            notification.error({ message: error.message ?? 'There was a problem saving your changes.' })
            if (pendingRollbackRef.current) {
                setContentItemViewNodes(pendingRollbackRef.current.previousNodes)
                pendingRollbackRef.current.pillIds.forEach((id) => removePendingAddPillId(id))
                pendingRollbackRef.current = null
            }
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem saving your changes. ${error}`,
                    variant: 'error',
                    id:
                        'WorkspaceEditor_InsertCustomManufacturerInProductListingError',
                },
            })
        },
    })

    // Existing pills on the same side (MFR or BOD) as this hook instance.
    // Memoized at hook level so both the button's popup list and the add
    // callback read from a single subscription.
    const existingPills = useMemo(
        () =>
            contentItemViewNodes.filter(
                (node) =>
                    (isBOD
                        ? node.isBOD
                        : node.isManufacturers && !node.isBOD) &&
                    node.dtvContentItemParentId != null
            ),
        [contentItemViewNodes, isBOD]
    )

    // Server-loaded pills have no top-level `text` (the GraphQL query selects
    // only the elements subtree); duplicate detection must read elements[0].text.
    // createOptimisticPillNode populates both fields, so optimistic adds also work.
    const localProductListings = useMemo(
        () =>
            existingPills.map((item) => ({
                listingText: item.elements?.[0]?.text,
            })),
        [existingPills]
    )

    const addCustomManufacturer = useCallback(
        (manufacturerName: string) => {
            if (!manufacturerName) return

            const newPillNode = createOptimisticPillNode({
                manufacturerName,
                existingPills,
                isBOD,
                productFamilyId,
                projectId,
                fallbackOrder: contentItemViewNodes.length,
            })
            // Shift any sibling whose order >= the new pill's order up by 1 so
            // we don't collide with the existing pill at that slot. TreeDataMapper
            // sorts children by `order`; ties fall back to array position, which
            // would render the new pill after the colliding sibling.
            const siblingIds = new Set(existingPills.map((p) => p.id))
            const newOrder = newPillNode.order ?? 0

            // BOD twin (MFR-side adds only). The twin's `order` is computed
            // against BOD siblings, and we must shift BOD-side siblings that
            // collide with it — the MFR-side shift above only covers MFR
            // siblings (Bug 2684545 / PR 192775 review).
            const bodTwinResult = !isBOD
                ? buildBodTwinPill(newPillNode, contentItemViewNodes)
                : undefined
            const bodShiftIds =
                bodTwinResult?.shiftedBodSiblingIds ?? new Set<string>()

            const updatedNodes = contentItemViewNodes.map((node) => {
                const mfrShift =
                    siblingIds.has(node.id) && (node.order ?? 0) >= newOrder
                const bodShift = node.id != null && bodShiftIds.has(node.id)
                return mfrShift || bodShift
                    ? { ...node, order: (node.order ?? 0) + 1 }
                    : node
            })

            const newPillIds = bodTwinResult
                ? [newPillNode.id!, bodTwinResult.twin.id!]
                : [newPillNode.id!]
            pendingRollbackRef.current = {
                previousNodes: contentItemViewNodes,
                pillIds: newPillIds,
            }

            setContentItemViewNodes(
                bodTwinResult
                    ? [...updatedNodes, newPillNode, bodTwinResult.twin]
                    : [...updatedNodes, newPillNode]
            )
            addPendingAddPillId(newPillNode.id!)
            if (bodTwinResult) addPendingAddPillId(bodTwinResult.twin.id!)

            const mutationInput = {
                elementId,
                isBOD,
                manufacturerName,
                productFamilyId,
                projectId,
                isAddedInOfficeMasterDraftProject: true,
            }

            notification.loading({ message: SAVING_NOTIFICATION_MESSAGE })

            insertCustomManufacturerInProductListing({
                variables: {
                    input: mutationInput,
                },
            }).then(async () => {
                // onError nulls the ref on failure before this runs — skip
                // the refetch/suppression so the rolled-back tree stays clean.
                if (!pendingRollbackRef.current) return
                const { pillIds } = pendingRollbackRef.current
                pendingRollbackRef.current = null
                // The mutation response doesn't include pillId, so the
                // optimistic pill(s) above still have client-generated ids.
                // Marking them for replacement makes the merge drop them so
                // the refetch swaps in the real server node cleanly.
                appendContentIdsToReplaceOnNextFetch?.(pillIds)
                try {
                    if (rootArticleId) {
                        await getContentItemViewNodesWithApolloClient(
                            [rootArticleId],
                            activeTab
                        )
                    }
                } finally {
                    // Keep isAddPending true until the refetch completes so the
                    // delete button stays hidden while the server node is in-flight.
                    pillIds.forEach((id) => removePendingAddPillId(id))
                    notification.success({ message: SAVE_SUCCESS_MESSAGE })
                }
            })
        },
        [
            isBOD,
            productFamilyId,
            projectId,
            elementId,
            rootArticleId,
            activeTab,
            existingPills,
            contentItemViewNodes,
            setContentItemViewNodes,
            appendContentIdsToReplaceOnNextFetch,
            addPendingAddPillId,
            removePendingAddPillId,
            insertCustomManufacturerInProductListing,
            getContentItemViewNodesWithApolloClient,
            notification,
        ]
    )

    return { addCustomManufacturer, localProductListings }
}
